import useSnackBar from '@/composables/snackbar';
import { APP_RESTART } from '@/lib/constants';
import { doSwitchAccount } from '@/api/sentinel';
import { failureCode, routeName } from 'shared/constants.json';
import { inject } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

// Re-bootstraps the app onto another tenant in-SPA (no full reload). SET_SWITCHING_ACCOUNT
// gates the main tree to loading while nebula resets and refetches; restart runs through the
// App.vue-provided APP_RESTART so data-sync subscriptions survive the picker unmounting. On
// failure, fall back to a hard reload so the user is never stranded.
export default function useAccountSwitch() {
  const store = useStore();
  const router = useRouter();
  const snackbar = useSnackBar();
  const restartApp = inject(APP_RESTART);

  // `resolveTarget(response)` runs after the switch so it can use the returned slug; app-start
  // then resolves the tenant from the target route. Resolves to whether the tenant actually
  // changed — callers that write to the new tenant afterwards would otherwise write to the old one.
  const rebootstrap = async (accountId, resolveTarget) => {
    if (store.state.switchingAccount) {
      return false;
    }

    store.commit('SET_SWITCHING_ACCOUNT', true);
    try {
      const response = await doSwitchAccount({ accountId });
      if (response?.status !== 200) {
        if (response?.data?.code !== failureCode.stepUpRequired) {
          snackbar.error();
        }
        return false;
      }

      store.dispatch('resetTenantState');
      await router.push(resolveTarget(response));
      await restartApp();
      return true;
    } catch {
      window.location.assign('/');
      return false;
    } finally {
      store.commit('SET_SWITCHING_ACCOUNT', false);
    }
  };

  // Push the account's slug so the router resolves it directly. Callers can override the
  // landing target (defaults to home). An account with no slug yet hasn't been named in
  // onboarding, so a tenant-scoped target can't resolve it and the router would re-inject
  // the previous account's slug — send it to the slugless onboarding entry instead.
  const switchAccount = (account, target = { name: routeName.home }) => {
    if (account.isCurrent) {
      return Promise.resolve(true);
    }
    return rebootstrap(account.uid, (response) => {
      const slug = response.data.slug || target.params?.tenantSlug;
      if (!slug) {
        return { name: routeName.onboarding };
      }
      return { ...target, params: { ...(target.params ?? {}), tenantSlug: slug } };
    });
  };

  return { switchAccount };
}
